import { IContractsResponse } from '@/api/contract/type'
import convertGregorianTimeToShamsiTime from '@/utils/functions/convertGregorianTimeToShamsiTime'
import { FC, ReactNode } from 'react'

const toFaDigits = (value: string) =>
  value.replace(/\d/g, (digit) => '۰۱۲۳۴۵۶۷۸۹'[Number(digit)] ?? digit)

const placeholder = (value?: string | null) =>
  value?.trim() || '«نام و نام خانوادگی»'

type LetterheadProps = {
  children: ReactNode
  date?: string | Date | null
  number?: string | number | null
  attachment?: string | null
  className?: string
  showSignatures?: boolean
  pageNumber?: number
  data?: IContractsResponse
}

const IconPhone: FC = () => (
  <svg width="11" height="11" viewBox="0 0 24 24" aria-hidden>
    <path
      fill="#fff"
      d="M6.6 10.8c1.4 2.8 3.8 5.2 6.6 6.6l2.2-2.2c.27-.27.67-.36 1.04-.24 1.2.4 2.48.62 3.8.62.55 0 1 .45 1 1V20c0 .55-.45 1-1 1C12.16 21 3 11.84 3 3c0-.55.45-1 1-1h3.44c.55 0 1 .45 1 1 0 1.32.22 2.6.62 3.8.12.37.03.77-.24 1.04L6.6 10.8z"
    />
  </svg>
)

const IconGlobe: FC = () => (
  <svg width="11" height="11" viewBox="0 0 24 24" aria-hidden>
    <path
      fill="none"
      stroke="#fff"
      strokeWidth="2"
      d="M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18z"
    />
    <path
      fill="none"
      stroke="#fff"
      strokeWidth="2"
      d="M3 12h18M12 3c2.5 2.6 3.8 5.8 3.8 9S14.5 18.4 12 21C9.5 18.4 8.2 15.2 8.2 12S9.5 5.6 12 3z"
    />
  </svg>
)

const IconMail: FC = () => (
  <svg width="11" height="11" viewBox="0 0 24 24" aria-hidden>
    <path
      fill="none"
      stroke="#fff"
      strokeWidth="2"
      d="M4 6h16v12H4z"
    />
    <path fill="none" stroke="#fff" strokeWidth="2" d="m4 7 8 6 8-6" />
  </svg>
)

const IconPin: FC = () => (
  <svg width="11" height="11" viewBox="0 0 24 24" aria-hidden>
    <path
      fill="#fff"
      d="M12 2.5c-3.6 0-6.5 2.8-6.5 6.3 0 4.7 6.5 12.7 6.5 12.7s6.5-8 6.5-12.7c0-3.5-2.9-6.3-6.5-6.3zm0 8.6a2.3 2.3 0 1 1 0-4.6 2.3 2.3 0 0 1 0 4.6z"
    />
  </svg>
)

const contacts = [
  { icon: <IconPhone />, text: '۰۲۱-۲۸۴۲۴۲۷۳' },
  { icon: <IconGlobe />, text: 'myapoloan.com', ltr: true },
  { icon: <IconMail />, text: 'salam@myapoloan.com', ltr: true },
  {
    icon: <IconPin />,
    text: 'تهران، خیابان آزادی، خیابان حبیب‌الله، پلاک ۵۶، ایستگاه نوآوری شریف',
  },
]

const MetaRow: FC<{ label: string; value?: string }> = ({ label, value }) => (
  <div className="letterhead-meta-row">
    <span className="letterhead-meta-label">{label}</span>
    <span className="letterhead-meta-value">{value || ''}</span>
  </div>
)

const Letterhead: FC<LetterheadProps> = ({
  children,
  date,
  number,
  attachment,
  className = '',
  showSignatures = true,
  pageNumber,
  data,
}) => {
  const shamsiDate = convertGregorianTimeToShamsiTime(date || new Date(), false, true)
  const numberText =
    number !== undefined && number !== null && String(number).trim() !== ''
      ? toFaDigits(String(number))
      : ''
  const acceptorName = placeholder(
    [data?.first_name, data?.last_name].filter(Boolean).join(' ')
  )

  return (
    <div className={`a4-page ${className}`.trim()}>
      <div className="letterhead-watermark">
        <img src="/assets/svg/logo.svg" alt="" />
      </div>

      <header className="letterhead-header">
        <div className="letterhead-brand">
          <div>
            <div className="letterhead-brand-mark">
              <img src="/assets/svg/logoLetterhead.svg" />
            </div>
            <div className="letterhead-reg">شماره ثبت: ۶۷۲۱۵۲</div>
          </div>
        </div>

        <div className="letterhead-meta">
          <MetaRow label="تاریخ:" value={shamsiDate} />
          <MetaRow label="شماره:" value={numberText} />
          <MetaRow label="پیوست:" value={attachment || 'ندارد'} />
        </div>
      </header>

      <div className="a4-page-body">{children}</div>

      <footer className="letterhead-footer">
        <div className="letterhead-contacts">
          {contacts.map((item) => (
            <div className="letterhead-contact" key={item.text}>
              <span className="letterhead-contact-icon">{item.icon}</span>
              <span
                className="letterhead-contact-text"
                dir={item.ltr ? 'ltr' : 'rtl'}
              >
                {item.text}
              </span>
            </div>
          ))}
        </div>

        {showSignatures && (
          <div className="letterhead-signatures">
            <div>
              <p className="font-bold">آقای رضا قنبرزاده میاندهی</p>
              <p>رئیس هیأت مدیره</p>
              <p>امضا و مهر</p>
              <p>(نوآوران کسب و کار آسان)</p>
            </div>
            <div>
              <p className="font-bold">آقای حسین نجفی زیارانی</p>
              <p>مدیرعامل</p>
              <p>امضا و مهر</p>
              <p>(نوآوران کسب و کار آسان)</p>
            </div>
            <div>
              <p className="font-bold">{acceptorName}</p>
              <p>امضا و مهر</p>
              <p>(پذیرنده)</p>
            </div>
          </div>
        )}

        {pageNumber != null && (
          <div className="letterhead-page-number">
            صفحه {toFaDigits(String(pageNumber))}
          </div>
        )}
      </footer>
    </div>
  )
}

export default Letterhead
